import React, { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, Link } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { CmsProvider, useCms } from "./CmsContext";
import { COLLECTIONS, timeAgo } from "./cmsUtils";
import { PublishModal } from "./PublishModal";
import { OverviewPage } from "./pages/OverviewPage";
import { PagesPage } from "./pages/PagesPage";
import { PageEditorPage } from "./pages/PageEditorPage";
import { CollectionPage } from "./pages/CollectionPage";
import { EntryEditorPage } from "./pages/EntryEditorPage";
import { MediaPage } from "./pages/MediaPage";
import { ActivityPage } from "./pages/ActivityPage";
import { TicketsPage } from "./pages/TicketsPage";

function SidebarLink({ to, end, children }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `lw-nav__link ${isActive ? "lw-nav__link--active" : ""}`}
    >
      {children}
    </NavLink>
  );
}

function Shell() {
  const { employee, changedCount, publishStatus, config } = useCms();
  const [publishOpen, setPublishOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="lwcms">
      <Toaster position="bottom-right" />
      <header className="lw-topbar">
        <div className="lw-topbar__left">
          <button
            type="button"
            className="lw-topbar__burger"
            aria-label="Toggle navigation"
            onClick={() => setNavOpen((v) => !v)}
          >
            ☰
          </button>
          <Link to="/landmark" className="lw-topbar__brand">
            <span className="lw-topbar__brandmark">L</span>
            <span className="lw-topbar__brandname">
              Landmark <strong>Website CMS</strong>
            </span>
          </Link>
        </div>
        <div className="lw-topbar__right">
          {config.siteUrl && (
            <a
              className="lw-btn lw-btn--ghost lw-btn--sm"
              href={config.siteUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View live site ↗
            </a>
          )}
          <button
            type="button"
            className={`lw-btn lw-btn--primary ${changedCount > 0 ? "lw-btn--pulse" : ""}`}
            onClick={() => setPublishOpen(true)}
          >
            Publish
            {changedCount > 0 && <span className="lw-badge lw-badge--count">{changedCount}</span>}
          </button>
        </div>
      </header>

      <div className="lw-layout">
        <nav className={`lw-nav ${navOpen ? "lw-nav--open" : ""}`} onClick={() => setNavOpen(false)}>
          <div className="lw-nav__section">
            <SidebarLink to="." end>
              Overview
            </SidebarLink>
            <SidebarLink to="pages">Pages</SidebarLink>
          </div>
          <div className="lw-nav__section">
            <p className="lw-nav__heading">Collections</p>
            {COLLECTIONS.map((c) => (
              <SidebarLink key={c.key} to={`collections/${c.key}`}>
                {c.label}
              </SidebarLink>
            ))}
          </div>
          <div className="lw-nav__section">
            <p className="lw-nav__heading">Workspace</p>
            <SidebarLink to="media">Media library</SidebarLink>
            <SidebarLink to="activity">Activity</SidebarLink>
            <SidebarLink to="tickets">Change requests</SidebarLink>
          </div>
          <div className="lw-nav__footer">
            {publishStatus.lastRelease && (
              <p className="lw-nav__deploy">
                Last publish {timeAgo(publishStatus.lastRelease.createdTime)}
                {publishStatus.lastRelease.triggeredBy?.fullName
                  ? ` by ${publishStatus.lastRelease.triggeredBy.fullName.split(" ")[0]}`
                  : ""}
              </p>
            )}
            {employee && <p className="lw-nav__user">{employee.fullName || employee.email}</p>}
          </div>
        </nav>

        <main className="lw-main">
          <Routes>
            <Route index element={<OverviewPage />} />
            <Route path="pages" element={<PagesPage />} />
            <Route path="pages/:docKey" element={<PageEditorPage />} />
            <Route path="collections/:collectionKey" element={<CollectionPage />} />
            <Route path="collections/:collectionKey/new" element={<EntryEditorPage isNew />} />
            <Route path="collections/:collectionKey/:slug" element={<EntryEditorPage />} />
            <Route path="media" element={<MediaPage />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="*" element={<Navigate to="." replace />} />
          </Routes>
        </main>
      </div>

      {publishOpen && <PublishModal onClose={() => setPublishOpen(false)} />}
    </div>
  );
}

export default function WebsiteCms() {
  // The global theme marks <body> as dark, whose !important heading colors
  // bleed into the CMS. Force light theme while this dashboard is mounted.
  useEffect(() => {
    const body = document.body;
    const previous = body.getAttribute("data-theme-version");
    body.setAttribute("data-theme-version", "light");
    return () => {
      if (previous) {
        body.setAttribute("data-theme-version", previous);
      } else {
        body.removeAttribute("data-theme-version");
      }
    };
  }, []);

  return (
    <CmsProvider>
      <Shell />
    </CmsProvider>
  );
}
